import { observable, action, makeObservable } from 'mobx';
import { v4 as uuid } from 'uuid';

import {
    ContactPersonData,
    EhrData as EhrResponseParams,
    EhrCreateForm,
    Optional,
    Channel,
    EhrTranslationData,
} from 'chaika-frontend-types/frontend-cms';
import { Locale, Sex } from 'chaika-enums';

import { InitParams, Form, ValidationType, ValueType } from '@store/abstract/Form';
import { Ehr } from '@patient/store/ehr/Ehr';
import { ContactPerson } from '@patient/store/ehr/models/ContactPerson';

import { ContactPersonForm } from './ContactPersonForm';
import { DateHelper, DateFormat } from '@utils/DateHelper';
import { assertIsDefined } from '@utils/assertIsDefined';

export class ProfileForm extends Form {
    public static Name = 'profileForm' as const;

    @observable
    public contactPersonForms: ContactPersonForm[] = [];

    protected storeName = Ehr.Name;

    @observable
    protected ehr: Ehr = new Ehr();

    constructor() {
        super();
        makeObservable(this);

        this.init();
    }

    @action
    public reset() {
        this.contactPersonForms = [];
        this.ehr = new Ehr();
    }

    @action
    public setEhr({ contactPersons = [], ...params }: EhrResponseParams) {
        this.ehr = new Ehr({ ...params, contactPersons: [] });

        this.contactPersonForms = contactPersons.map(contactPerson => new ContactPersonForm(contactPerson));
    }

    @action
    public setFieldTranslations(fieldName: string, value: string, locale: Locale): void {
        const translation = this.ehr.translations?.get(locale) ?? {};
        this.ehr.translations.set(locale, { ...translation, locale, [fieldName]: value });
    }

    public getTranslations(): Map<Locale, EhrTranslationData> {
        return this.ehr.translations ?? new Map();
    }

    public getCreateParams(): EhrCreateForm {
        return {
            id: uuid(),
            firstName: this.ehr.firstName,
            lastName: this.ehr.lastName,
            patronymic: this.ehr.patronymic || undefined,
            sex: this.ehr.sex as any as Sex,
            birthDate: DateHelper.format(this.ehr.getDateBirthDate(), DateFormat.DateWithDashSeparator),
            phone: this.ehr.phone,
            whatsApp: this.ehr.phone,
            contactPersons: this.contactPersonForms.map(form => form.serialize()) as any[],
            primaryChannel: Channel.WhatsApp,
        };
    }

    public getFormattedFieldValue(name: string): ValueType {
        return super.getFieldValue(name);
    }

    public getContactPerson(id: string): ContactPersonForm {
        const contactPerson = this.contactPersonForms.find(person => person.technicalId === id);
        assertIsDefined(contactPerson, `Contact person ${id} is rendered from the profile form contact persons`);
        return contactPerson;
    }

    public getContactPersonIds(): string[] {
        return this.contactPersonForms.map(person => person.technicalId);
    }

    public getDateBirthDate(): Optional<Date> {
        return this.ehr.getDateBirthDate();
    }

    @action
    public removeContactPerson(removedContactPerson: ContactPerson) {
        this.contactPersonForms = this.contactPersonForms.filter(
            contactPerson =>
                contactPerson.fullName !== removedContactPerson.fullName &&
                contactPerson.getFieldValue('phone') !== removedContactPerson.phone,
        );
    }

    @action
    public removeContactPersonById(id: string) {
        this.contactPersonForms = this.contactPersonForms.filter(contactPerson => contactPerson.technicalId !== id);
    }

    public serializeContactPersons(): ContactPersonData[] {
        return this.contactPersonForms.map(person => person.serialize());
    }

    public addContactPersons(params: ContactPersonData) {
        return this.contactPersonForms.push(new ContactPersonForm(params));
    }

    protected getInitParams(): InitParams[] {
        return [
            {
                name: 'firstName',
                isRequired: true,
                format: this.formatName,
                validation: [ValidationType.notEmpty, ValidationType.Name],
            },
            {
                name: 'lastName',
                isRequired: true,
                format: this.formatName,
                validation: [ValidationType.notEmpty, ValidationType.Name],
            },
            {
                name: 'patronymic',
                validation: [ValidationType.Name],
                format: this.formatName,
            },
            {
                name: 'birthDate',
                isRequired: true,
                validation: [ValidationType.notEmpty, ValidationType.Birthday],
            },
            {
                name: 'phone',
                format: this.onlyNumbers,
                isRequired: true,
                validation: [ValidationType.notEmpty, ValidationType.PhoneString],
            },
            {
                name: 'email',
                validation: [ValidationType.Email],
            },
            {
                name: 'snils',
                format: this.onlyNumbers,
                validation: [ValidationType.Snils],
            },
            {
                name: 'inn',
                validation: [ValidationType.Inn],
            },
        ];
    }

    protected formatName(name: string) {
        return name.replace(/[a-zа-яё]+/gi, value => value.slice(0, 1).toUpperCase() + value.slice(1).toLowerCase());
    }
}

export const profileForm = new ProfileForm();
